"use client";

import Image from "next/image";
import { useState } from "react";
import type { GalleryImage, PageDetail } from "@/types/page";
import type { TenantSettings } from "@/types/tenant";
import { BlockRenderer } from "@/components/blocks/BlockRenderer";
import { getSiteTemplateKey } from "@/lib/siteTemplates";
import { GalleryLightbox } from "@/components/gallery/GalleryLightbox";

interface GalleryPageClientProps {
  page: PageDetail;
  images: GalleryImage[];
  tenantSettings?: TenantSettings;
}

export function GalleryPageClient({ page, images, tenantSettings }: GalleryPageClientProps) {
  const layout = page.layout || "grid-3";
  const template = getSiteTemplateKey(tenantSettings);
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);

  const gridClass =
    layout === "masonry"
      ? "columns-1 gap-4 sm:columns-2 lg:columns-3"
      : layout === "grid-4"
        ? "grid gap-4 grid-cols-2 lg:grid-cols-4"
        : "grid gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3";

  return (
    <section className="mx-auto max-w-6xl px-4 py-8">
      <BlockRenderer blocks={page.blocks} template={template} heroStartsAtH2 />
      <h1 className="mb-6 text-3xl font-bold" style={{ fontFamily: "var(--font-heading)" }}>
        {page.title}
      </h1>
      <ul className={gridClass}>
        {images.map((img, index) => (
          <li
            key={img.id}
            className={layout === "masonry" ? "mb-4 break-inside-avoid" : ""}
          >
            <button
              type="button"
              className="group relative block min-h-[44px] w-full cursor-zoom-in overflow-hidden rounded-[var(--border-radius)] bg-slate-100 text-left"
              onClick={() => setLightboxIndex(index)}
              aria-label={`Open ${img.altText || img.caption || `gallery image ${index + 1}`} full size`}
            >
              <Image
                src={img.url}
                alt={img.altText ?? ""}
                width={600}
                height={400}
                className="h-auto max-h-[32rem] w-full object-contain object-top transition group-hover:opacity-90"
                sizes="(max-width: 768px) 100vw, 33vw"
              />
              {img.caption && (
                <span className="mt-1 block text-sm text-slate-600">{img.caption}</span>
              )}
            </button>
          </li>
        ))}
      </ul>
      <GalleryLightbox
        images={images}
        activeIndex={lightboxIndex}
        onChange={setLightboxIndex}
        onClose={() => setLightboxIndex(null)}
      />
    </section>
  );
}
